import React, { useCallback, useEffect, useReducer, useState } from 'react';

import {
  Auth0Client,
  Auth0ClientOptions,
  PopupLoginOptions,
  PopupConfigOptions,
  GetTokenWithPopupOptions,
  CacheLocation,
  LogoutOptions,
  RedirectLoginOptions as Auth0RedirectLoginOptions,
  GetTokenSilentlyOptions,
} from '@auth0/auth0-spa-js';
import { useHistory } from 'react-router-dom';

import { generateRandomString } from '../../utils/helpers';
import { RedirectLoginOptions } from './AuthContext';
import { AuthContext } from './AuthContext';
import { initialAuthState } from './AuthState';
import { reducer } from './reducer';
import { hasAuthParams, loginError, tokenError } from './utils';

export type AppState = {
  returnTo?: string;
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export interface AuthProviderOptions {
  children?: React.ReactNode;
  onRedirectCallback?: (appState: AppState, history?: any) => void;
  loginCallback?: () => void;
  skipRedirectCallback?: boolean;
  domain: string;
  issuer?: string;
  clientId: string;
  redirectUri?: string;
  leeway?: number;
  cacheLocation?: CacheLocation;
  useRefreshTokens?: boolean;
  authorizeTimeoutInSeconds?: number;
  advancedOptions?: {
    defaultScope?: string;
  };
  maxAge?: string | number;
  audience?: string;
  organization?: string;
  invitation?: string;
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

/**
 * @ignore
 */
const toAuth0ClientOptions = (opts: AuthProviderOptions): Auth0ClientOptions => {
  const { clientId, redirectUri, maxAge, ...validOpts } = opts;
  return {
    ...validOpts,
    client_id: clientId,
    redirect_uri: redirectUri,
    max_age: maxAge,
  };
};

/**
 * @ignore
 */
const toAuth0LoginRedirectOptions = (
  opts?: RedirectLoginOptions,
): Auth0RedirectLoginOptions | undefined => {
  if (!opts) {
    return;
  }
  const { redirectUri, ...validOpts } = opts;
  return {
    ...validOpts,
    redirect_uri: redirectUri,
  };
};

/**
 * @ignore
 */
const defaultOnRedirectCallback = (appState?: AppState, history?): void => {
  let returnTo = `/`;
  const redirectCode = appState?.redirectCode;
  const isDirectUrl = appState?.isDirectUrl;

  if (!isDirectUrl) {
    if (redirectCode) {
      const returnUrl = localStorage.getItem(redirectCode);
      returnTo = returnUrl || returnTo;
      localStorage.removeItem(redirectCode);
    }

    if (history) {
      history.replace(returnTo);
    }
  } else {
    if (window?.location?.search && history) {
      history.replace(window?.location?.pathname);
    }
  }
};

/**
 * ```jsx
 * <Auth0Provider
 *   domain={domain}
 *   clientId={clientId}
 *   redirectUri={window.location.origin}>
 *   <MyApp />
 * </Auth0Provider>
 * ```
 *
 * Provides the Auth0Context to its child components.
 */
const AuthProvider = (opts: AuthProviderOptions): JSX.Element => {
  const {
    children,
    skipRedirectCallback,
    onRedirectCallback = defaultOnRedirectCallback,
    loginCallback,
    ...clientOpts
  } = opts;
  const [client, setClient] = useState<Auth0Client | null>(null);
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const history = useHistory();

  useEffect(() => {
    if (!client && clientOpts.clientId) {
      setClient(() => new Auth0Client(toAuth0ClientOptions(clientOpts)));
      dispatch({ type: 'CLIENT_INITIALISED' });
    }
  }, [client, clientOpts]);

  useEffect(() => {
    (async (): Promise<void> => {
      if (client) {
        try {
          if (hasAuthParams() && !skipRedirectCallback) {
            const { appState } = await client.handleRedirectCallback();
            loginCallback && loginCallback();
            onRedirectCallback(appState, history);
          } else {
            await client?.checkSession();
          }
          const user = await client?.getUser();
          dispatch({ type: 'INITIALISED', user });
        } catch (error) {
          dispatch({ type: 'ERROR', error: loginError(error) });
        }
      }
    })();
  }, [client, onRedirectCallback, skipRedirectCallback, history, loginCallback]);

  const loginWithRedirect = useCallback(
    (opts?: RedirectLoginOptions): Promise<void> => {
      if (client) {
        const redirectUri = opts?.redirectUri;
        const mode = opts?.mode || 'login';
        const isDirectUrl = opts?.isDirectUrl;

        const redirectUrl = redirectUri || '/';

        let appState = {};

        if (!isDirectUrl) {
          //redirect random code
          const redirectCode = generateRandomString(20);
          //add redirect url with code to local storage
          localStorage.setItem(redirectCode, redirectUrl);

          appState = {
            redirectCode: redirectCode,
          };
        } else {
          appState = {
            isDirectUrl: true,
          };
        }

        return client.loginWithRedirect(
          toAuth0LoginRedirectOptions({
            ...opts,
            redirectUri: isDirectUrl
              ? `${window?.location?.origin}${redirectUrl}`
              : `${window?.location?.origin}`,
            appState,
            mode: mode,
          }),
        );
      } else {
        return new Promise<void>((res) => {
          res();
        });
      }
    },
    [client],
  );

  const loginWithPopup = useCallback(
    async (options?: PopupLoginOptions, config?: PopupConfigOptions): Promise<void> => {
      if (client) {
        dispatch({ type: 'LOGIN_POPUP_STARTED' });
        try {
          await client.loginWithPopup(options, config);
        } catch (error) {
          dispatch({ type: 'ERROR', error: loginError(error) });
          return;
        }
        const user = await client.getUser();
        dispatch({ type: 'LOGIN_POPUP_COMPLETE', user });
      } else {
        return new Promise<void>((res) => {
          res();
        });
      }
    },
    [client],
  );

  const logout = useCallback(
    (opts: LogoutOptions = {}): void => {
      client?.logout({
        returnTo: window?.location?.origin,
        ...opts,
      });
      if (opts.localOnly) {
        dispatch({ type: 'LOGOUT' });
      }
    },
    [client],
  );

  const getAccessTokenSilently = useCallback(
    async (opts?: GetTokenSilentlyOptions): Promise<string> => {
      let token;
      try {
        token = await client?.getTokenSilently(opts);
      } catch (error) {
        throw tokenError(error);
      } finally {
        dispatch({
          type: 'GET_ACCESS_TOKEN_COMPLETE',
          user: await client?.getUser(),
        });
      }
      return token;
    },
    [client],
  );

  const getAccessTokenWithPopup = useCallback(
    async (opts?: GetTokenWithPopupOptions, config?: PopupConfigOptions): Promise<string> => {
      if (client) {
        let token;
        try {
          token = await client.getTokenWithPopup(opts, config);
        } catch (error) {
          throw tokenError(error);
        } finally {
          dispatch({
            type: 'GET_ACCESS_TOKEN_COMPLETE',
            user: await client.getUser(),
          });
        }
        return token;
      } else {
        return new Promise<string>((res) => {
          res();
        });
      }
    },
    [client],
  );

  const updateUserInfo = useCallback((user) => {
    dispatch({
      type: 'UPDATE_USER_INFO',
      userInfo: user,
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        getAccessTokenSilently,
        loginWithRedirect,
        logout,
        updateUserInfo,
        loginWithPopup,
        getAccessTokenWithPopup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider };
